import React, { useEffect, useState, useRef } from 'react';

interface IframeWithDynamicHeightProps {
  htmlContent: string;
}

const IframeWithDynamicHeight: React.FC<IframeWithDynamicHeightProps> = ({
  htmlContent,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(0); // Default height

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      try {
        // Get the document height from the iframe content
        const iframeDocument =
          iframe.contentDocument || iframe.contentWindow?.document;

        if (iframeDocument) {
          // Apply Instrument Sans font to the iframe content
          const styleElement = iframeDocument.createElement('style');
          styleElement.textContent = `
            body, html {
              font-family: 'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
              font-size: 14px;
              line-height: 1.5;
              margin: 0;
              padding: 0;
            }
          `;
          iframeDocument.head.appendChild(styleElement);

          // Intercept link clicks to open them in the parent window
          iframeDocument.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            const link = target.closest('a');
            if (link && link.href) {
              e.preventDefault();
              window.open(link.href, '_blank', 'noopener,noreferrer');
            }
          });

          // Get the scroll height of the document body
          const docHeight = iframeDocument.documentElement.scrollHeight;
          setHeight(docHeight + (docHeight > 300 ? 50 : 0)); // Add a little extra space to avoid scrollbars. Only needed if we think it's likely to be bigger than the screen
        }
      } catch (error) {
        console.error('Error adjusting iframe height:', error);
      }
    };

    iframe.addEventListener('load', handleLoad);

    // Also set a timeout as a fallback for content that might load after the initial load event
    const timeoutId = setTimeout(handleLoad, 500);

    return () => {
      iframe.removeEventListener('load', handleLoad);
      clearTimeout(timeoutId);
    };
  }, [htmlContent]);

  // Create a wrapped HTML content that includes the Instrument Sans font styles
  const wrappedHtmlContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <meta http-equiv="Content-Security-Policy" content="
          default-src 'self';
          script-src 'self' 'unsafe-inline';
          style-src 'self' 'unsafe-inline' https://fonts.googleapis.com;
          font-src 'self' https://fonts.gstatic.com;
          img-src 'self' data: https:;
          connect-src 'none';
          object-src 'none';
          frame-src 'none';
          form-action 'none';
        ">
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Instrument+Sans:ital,wght@0,400..700;1,400..700&display=swap');
          
          body, html {
            font-family: 'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            font-size: 14px;
            line-height: 1.5;
            margin: 0;
            padding: 0;
          }
          
          /* Preserve email styles but ensure our font settings take precedence */
          * {
            font-family: inherit;
          }
          
          /* Ensure links are styled properly */
          a {
            color: #0066cc;
            text-decoration: underline;
          }
          
          /* Basic table styling */
          table {
            border-collapse: collapse;
            width: 100%;
          }
          
          td, th {
            padding: 8px;
          }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
    </html>
  `;

  return (
    <iframe
      ref={iframeRef}
      srcDoc={wrappedHtmlContent}
      style={{
        width: '100%',
        height: `${height}px`,
        border: 'none',
        overflow: 'hidden',
      }}
      title='Email content'
      sandbox='allow-same-origin allow-popups allow-scripts'
    />
  );
};

export default IframeWithDynamicHeight;
